import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatHillTime } from './utils';

const modeConfig = {
  HP: {
    emptyMessage: 'No Hardpoint insights available.',
    columns: [
      { key: 'Player', label: 'Player' },
      { key: 'Objective', label: 'Objective Time', title: 'avgHillTime = hillTimeTotal / matches' },
      { key: 'HillBreaker', label: 'Objective Kills', title: 'hillBreaker = captureKillsTotal / matches' },
      { key: 'PressurePlayer', label: 'Pressure', title: 'pressureScore = (damageTotal + distanceTotal) / matches' },
      { key: 'OperatorImpact', label: 'Operator Impact', title: 'operatorImpact = operatorValueTotal / matches' },
      { key: 'Survivability', label: 'Survivability', title: 'Percentage of engagements survived. Calculated as K / (K + D).' },
    ],
    defaultSortBy: 'Objective',
  },
  SND: {
    emptyMessage: 'No Search & Destroy insights available.',
    columns: [
      { key: 'Player', label: 'Player' },
      { key: 'ADR', label: 'ADR', title: 'Average damage dealt per round. (avg ADR)' },
      { key: 'FirstKills', label: 'First Bloods', title: 'Average first bloods per match. (FirstBloods / Matches)' },
      { key: 'Clutches1v1', label: '1v1 Clutches', title: 'Average successful 1v1 clutch wins per match. (Clutches1v1 / Matches)' },
      { key: 'Survivability', label: 'Survivability', title: 'Percentage of engagements survived. Calculated as K / (K + D).' },
    ],
    defaultSortBy: 'ADR',
  },
  CTL: {
    emptyMessage: 'No Control insights available.',
    columns: [
      { key: 'Player', label: 'Player' },
      { key: 'ADR', label: 'ADR', title: 'Damage per round in Control. Calculated as TotalDamage / (PF + PA).' },
      { key: 'ObjectiveLeader', label: 'Objective Score', title: 'Capture kills plus impact score. Calculated as CaptureKills + Impact.' },
      { key: 'OperatorImpact', label: 'Operator Impact', title: 'Average impact score generated by operator usage. (avg OperatorValue)' },
      { key: 'SurvivabilityLeader', label: 'Survivability', title: 'Percentage of engagements survived. Calculated as K / (K + D).' },
    ],
    defaultSortBy: 'ADR',
  },
};

const totalKeyMap = {
  HP: {
    Objective: 'ObjectiveTotal',
    HillBreaker: 'HillBreakerTotal',
    PressurePlayer: 'PressurePlayerTotal',
    OperatorImpact: 'OperatorImpactTotal',
    Survivability: 'Survivability',
  },
  SND: {
    ADR: 'ADRTotal',
    FirstKills: 'FirstKillsTotal',
    Clutches1v1: 'Clutches1v1Total',
    Survivability: 'Survivability',
  },
  CTL: {
    ADR: 'ADRTotal',
    ObjectiveLeader: 'ObjectiveLeaderTotal',
    OperatorImpact: 'OperatorImpactTotal',
    SurvivabilityLeader: 'SurvivabilityLeader',
  },
};

const RankingsGrid = ({ mode, rankings, onPlayerClick, playerImages = {}, statsView = 'avg' }) => {
  const activeConfig = modeConfig[mode];
  const rows = useMemo(() => (Array.isArray(rankings) ? rankings : []), [rankings]);
  const [sortBy, setSortBy] = useState(activeConfig?.defaultSortBy || 'Player');
  const [sortDir, setSortDir] = useState('desc');

  const getMetricValue = useCallback((row, key) => {
    if (statsView === 'totals') {
      const mappedKey = totalKeyMap[mode]?.[key];
      if (mappedKey) return row?.[mappedKey];
    }
    return row?.[key];
  }, [mode, statsView]);

  const formatMetric = useCallback((value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) return '0';
    if (statsView === 'avg') return n.toFixed(1);
    return n.toLocaleString(undefined, { maximumFractionDigits: 20 });
  }, [statsView]);

  useEffect(() => {
    if (!activeConfig) return;
    setSortBy(activeConfig.defaultSortBy);
    setSortDir('desc');
  }, [activeConfig, mode]);

  const sorted = useMemo(() => {
    const next = [...rows].sort((a, b) => {
      const aVal = getMetricValue(a, sortBy);
      const bVal = getMetricValue(b, sortBy);
      if (sortBy === 'Player') {
        const aText = String(aVal || '');
        const bText = String(bVal || '');
        return sortDir === 'asc' ? aText.localeCompare(bText) : bText.localeCompare(aText);
      }
      const aNum = Number(aVal || 0);
      const bNum = Number(bVal || 0);
      return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
    });
    return next;
  }, [rows, sortBy, sortDir, getMetricValue]);

  if (!activeConfig) return null;

  if (!rows.length) {
    return <div style={{ color: '#888', textAlign: 'center', padding: '20px' }}>{activeConfig.emptyMessage}</div>;
  }

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortDir(key === 'Player' ? 'asc' : 'desc');
  };

  return (
    <div className="map-performance-container">
      <div className="table-wrapper">
        <table className="performance-table">
          <thead>
            <tr>
              {activeConfig.columns.map((col) => (
                <th key={col.key} title={col.title || ''}>
                  <button
                    type="button"
                    onClick={() => toggleSort(col.key)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      fontWeight: 700,
                      padding: 0,
                    }}
                  >
                    {col.label}
                    {sortBy === col.key ? (sortDir === 'asc' ? ' ^' : ' v') : ''}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={`${String(mode).toLowerCase()}-insight-${row.Player}`}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {playerImages[row.Player] ? (
                      <img
                        src={playerImages[row.Player]}
                        alt={row.Player}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2px solid #7E57C2',
                        }}
                      />
                    ) : null}
                    <button className="player-name-link" onClick={() => onPlayerClick(row.Player)}>
                      {row.Player}
                    </button>
                  </div>
                </td>
                {mode === 'HP' ? (
                  <>
                    <td>{formatHillTime(getMetricValue(row, 'Objective'))}</td>
                    <td>{formatMetric(getMetricValue(row, 'HillBreaker'))}</td>
                    <td>{formatMetric(getMetricValue(row, 'PressurePlayer'))}</td>
                    <td>{formatMetric(getMetricValue(row, 'OperatorImpact'))}</td>
                    <td>{Math.round(Number(row.Survivability || 0) * 100)}%</td>
                  </>
                ) : mode === 'SND' ? (
                  <>
                    <td>{formatMetric(getMetricValue(row, 'ADR'))}</td>
                    <td>{formatMetric(getMetricValue(row, 'FirstKills'))}</td>
                    <td>{formatMetric(getMetricValue(row, 'Clutches1v1'))}</td>
                    <td>{Math.round(Number(row.Survivability || 0) * 100)}%</td>
                  </>
                ) : (
                  <>
                    <td>{formatMetric(getMetricValue(row, 'ADR'))}</td>
                    <td>{formatMetric(getMetricValue(row, 'ObjectiveLeader'))}</td>
                    <td>{formatMetric(getMetricValue(row, 'OperatorImpact'))}</td>
                    <td>{Math.round(Number(row.SurvivabilityLeader || 0) * 100)}%</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RankingsGrid;
